import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardTasksService from '@satellite/services/yard-tasks-service';

import { transformTaskApiResponseToViewData } from '../helpers/yardTaskUtils';

import type { ComputedRef, Ref } from 'vue';
import type { TaskApiResponseData, TaskViewData } from '@nova/core';

export interface UseYardTaskFetcherOptions {
  taskId?: Ref<string | null | undefined>;
  // initialTask is already transformed (view-ready format) and used only for initial state
  initialTask?:
    | TaskViewData
    | Ref<TaskViewData | null | undefined>
    | ComputedRef<TaskViewData | null | undefined>
    | null;
  /**
   * If true, fetches immediately when component mounts (if no initialTask provided).
   * If false, the task must be fetched manually using the refresh method.
   * Default: true
   */
  immediate?: boolean;
  /**
   * If true, the task will be refreshed automatically when the task is updated (via eventHub).
   * The task must be refreshed manually using the refresh method.
   */
  live?: boolean;
}

export interface YardTaskFetcherDependencies {
  warehouseTimezone?: string | Ref<string | undefined> | (() => string | undefined);
}

export function useYardTaskFetcher(
  options: UseYardTaskFetcherOptions = {},
  dependencies?: YardTaskFetcherDependencies
) {
  const { taskId, initialTask, immediate, live } = { immediate: true, live: false, ...options };
  const { warehouseTimezone: providedTimezone } = dependencies || {};

  const eventHub = useEventHub();

  const task = ref<TaskViewData | null>(toValue(initialTask) || null);
  const isLoading = ref(false);
  const rawTask = ref<TaskApiResponseData | null>(null);

  const warehouseTimezone = computed(() => {
    return toValue(providedTimezone) || 'utc';
  });

  const effectiveTaskId = computed(() => {
    const explicitTaskId = unref(taskId);

    if (explicitTaskId) {
      /**
       * Warn if both are provided but don't match.
       */
      const initialTaskId = unref(initialTask)?.id;

      if (initialTaskId && initialTaskId !== explicitTaskId) {
        console.warn(
          '[useYardTaskFetcher] taskId mismatch:',
          `explicit taskId (${explicitTaskId}) does not match initialTask.id (${initialTaskId}). Using explicit taskId.`
        );
      }

      return explicitTaskId;
    }

    return unref(initialTask)?.id || null;
  });

  const fetchTask = async (id: string) => {
    if (!id) {
      return;
    }

    isLoading.value = true;

    try {
      const response = (await yardTasksService.getYardTask(id)) as TaskApiResponseData;
      rawTask.value = response;

      if (response) {
        task.value = transformTaskApiResponseToViewData(response);
      } else {
        task.value = null;
      }
    } catch (error) {
      rawTask.value = null;
      task.value = null;

      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = async (onError?: (error: Error) => void): Promise<void> => {
    const id = effectiveTaskId.value;
    if (!id) {
      // No taskId available (neither explicit nor extracted from initialTask)
      rawTask.value = null;
      task.value = null;
      return;
    }

    try {
      await fetchTask(id);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[useYardTaskFetcher] Failed to refresh task:', error);

      if (onError) {
        onError(error);
      }

      // throw error for callers that await
      throw error;
    }
  };

  const handleUpdateYardTaskEvent = async (event: { id: string | null }) => {
    if (event?.id === effectiveTaskId.value) {
      // Fire-and-forget for automatic event-driven refreshes
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }
  };

  /**
   * Watch explicit taskId only (not extracted from initialTask)
   */
  watch(
    () => unref(taskId),
    async newId => {
      if (newId) {
        // Fire-and-forget for automatic watcher-driven refreshes
        try {
          await refresh();
        } catch {
          // Error already logged in refresh()
        }
      } else {
        rawTask.value = null;
        task.value = null;
      }
    }
  );

  onMounted(async () => {
    /**
     * Only fetch if immediate is true and no initialTask was provided (trust initial data)
     */
    if (immediate && !unref(initialTask)) {
      // Fire-and-forget for automatic initial fetch
      try {
        await refresh();
      } catch {
        // Error already logged in refresh()
      }
    }

    if (live) {
      eventHub.on('update-YardTask', handleUpdateYardTaskEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-YardTask', handleUpdateYardTaskEvent);
    }
  });

  return {
    rawTask,
    task,
    isLoading,
    refresh,
    warehouseTimezone
  };
}
